<template>
  <el-dialog :visible.sync="visible" :title="$t('depositRisk.transactionInfo')" custom-class="deposit-transaction-dialog" width="500px" top="6vh" append-to-body @closed="onClosed">
    <div v-loading="loading" class="deposit-transaction-content">
      <el-alert v-if="error" :title="error" type="error" :closable="false" />
      <template v-if="record">
        <section class="deposit-result" :class="statusTone" aria-live="polite">
          <i :class="statusIcon" aria-hidden="true"></i>
          <h2>{{ $t('depositRisk.' + (isFrozen ? 'fundsFrozen' : record.displayStatus)) }}</h2>
          <p>{{ $t('depositRisk.' + (isCredited ? 'credited' : isFrozen ? 'highRiskSource' : 'pending')) }}</p>
        </section>
        <dl class="deposit-facts">
          <div><dt>{{ $t('depositRisk.coin') }}</dt><dd>{{ record.assetType || '—' }}<template v-if="record.network">({{ record.network }})</template></dd></div>
          <div><dt>{{ $t('depositRisk.amount') }}</dt><dd>{{ amount }} {{ record.assetType || '' }}</dd></div>
          <div><dt>{{ $t('depositRisk.hash') }}</dt><dd><span class="deposit-hash" :title="record.txHash">{{ shortHash }}</span></dd></div>
          <div><dt>{{ $t('depositRisk.created') }}</dt><dd>{{ formatTime(record.createTime) }}</dd></div>
          <div><dt>{{ $t('depositRisk.creditedAt') }}</dt><dd>{{ isCredited ? formatTime(record.creditedAt || record.confirmTime) : '—' }}</dd></div>
          <div><dt>{{ $t('depositRisk.security') }}</dt><dd :class="securityTone">{{ $t('depositRisk.' + securityStatus) }}</dd></div>
        </dl>
        <el-button v-if="isCredited" class="deposit-balance-button" type="primary" @click="balance">{{ $t('depositRisk.asset') }}</el-button>
        <div v-else-if="isFrozen || record.displayStatus === 'REVIEW_PENDING' || record.displayStatus === 'REJECTED'" class="deposit-contact">
          <p>{{ $t('depositRisk.contact') }}</p>
          <a :href="'mailto:' + supportEmail">{{ supportEmail }}</a>
        </div>
      </template>
      <div v-if="error || (record && !isCredited && !isFrozen)" class="deposit-refresh">
        <el-button type="text" :disabled="loading" @click="refresh">{{ $t('depositRisk.refresh') }}</el-button>
      </div>
    </div>
  </el-dialog>
</template>
<script>
import { depositDisplayStatus } from "./depositStatus";
export default {
  data() { return { visible: false, loading: false, error: '', id: null, record: null, sequence: 0, supportEmail: process.env.VUE_APP_SUPPORT_EMAIL || 'support@rwa.io' } },
  computed: {
    isCredited() { return !!this.record && this.record.displayStatus === 'CREDITED' },
    isFrozen() { return !!this.record && this.record.displayStatus === 'FROZEN' },
    statusTone() { return this.isCredited ? 'is-success' : this.isFrozen || this.record.displayStatus === 'REJECTED' ? 'is-danger' : 'is-pending' },
    statusIcon() { return this.isCredited ? 'el-icon-success' : this.isFrozen || this.record.displayStatus === 'REJECTED' ? 'el-icon-error' : 'el-icon-time' },
    amount() {
      const raw = this.record && this.record.value
      if (raw == null) return '—'
      const parts = String(raw).split('.')
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      if (parts[1]) parts[1] = parts[1].replace(/0+$/, '')
      return parts.filter(Boolean).join('.')
    },
    shortHash() { const hash = this.record && this.record.txHash; return !hash ? '—' : hash.length > 20 ? hash.slice(0, 6) + '…' + hash.slice(-6) : hash },
    securityStatus() {
      const status = this.record.securityReviewStatus
      if (status) return status
      if (this.isFrozen) return 'HIGH_RISK'
      if (this.record.displayStatus === 'REVIEW_PENDING') return 'UNDER_REVIEW'
      return this.isCredited ? 'UNKNOWN' : 'PENDING'
    },
    securityTone() { return ['PASSED', 'MANUAL_APPROVED'].includes(this.securityStatus) ? 'is-success' : this.securityStatus === 'HIGH_RISK' ? 'is-danger' : '' }
  },
  methods: {
    open(row) { this.id = row.id; this.record = null; this.visible = true; this.refresh() },
    onClosed() { this.sequence++; this.record = null; this.loading = false },
    formatTime(value) {
      if (value == null || value === '') return '—'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '—'
      const pad = n => String(n).padStart(2, '0')
      return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate()) + ' ' + pad(date.getHours()) + ':' + pad(date.getMinutes()) + ':' + pad(date.getSeconds())
    },
    async refresh() {
      const sequence = ++this.sequence; this.loading = true; this.error = ''
      try {
        const response = await this.$axios.post('/userAssets/tranRecordDetail', { id: this.id, queryType: 'deposit' })
        if (sequence !== this.sequence) return
        if (response.data.code !== '000000' || !response.data.data || !response.data.data.deposit) throw new Error()
        const row = response.data.data.deposit
        this.record = { ...row, displayStatus: depositDisplayStatus(row) }
      } catch (e) { if (sequence === this.sequence) { this.record = null; this.error = this.$t('depositRisk.unavailable') } }
      finally { if (sequence === this.sequence) this.loading = false }
    },
    balance() { this.visible = false; this.$emit('balance') }
  }
}
</script>
<style>
.deposit-transaction-dialog { max-width: calc(100vw - 32px); border-radius: 12px; color: #393939; font-family: Arial, "Microsoft YaHei", sans-serif; }
.deposit-transaction-dialog .el-dialog__header { padding: 34px 34px 0; }
.deposit-transaction-dialog .el-dialog__title { font-size: 28px; line-height: 40px; font-weight: 600; color: #393939; }
.deposit-transaction-dialog .el-dialog__headerbtn { top: 18px; right: 18px; font-size: 26px; }
.deposit-transaction-dialog .el-dialog__body { padding: 20px 34px 24px; color: #454545; }
.deposit-transaction-dialog .deposit-transaction-content { min-height: 180px; }
.deposit-transaction-dialog .deposit-result { text-align: center; padding-bottom: 44px; border-bottom: 1px solid #e3e3e3; }
.deposit-transaction-dialog .deposit-result > i { font-size: 72px; }
.deposit-transaction-dialog .deposit-result h2 { font-size: 22px; font-weight: 500; line-height: 32px; margin: 14px 0 8px; }
.deposit-transaction-dialog .deposit-result p { font-size: 18px; line-height: 28px; color: #555; margin: 0; }
.deposit-transaction-dialog .is-success { color: #27bf8a; }
.deposit-transaction-dialog .is-danger { color: #ec5900; }
.deposit-transaction-dialog .is-pending { color: #e99a20; }
.deposit-transaction-dialog .deposit-facts { margin: 30px 0 24px; font-size: 18px; line-height: 26px; }
.deposit-transaction-dialog .deposit-facts > div { display: flex; justify-content: space-between; align-items: baseline; gap: 16px; padding: 11px 0; }
.deposit-transaction-dialog .deposit-facts dt { flex-shrink: 0; color: #999; }
.deposit-transaction-dialog .deposit-facts dd { margin: 0; text-align: right; overflow-wrap: anywhere; font-variant-numeric: tabular-nums; }
.deposit-transaction-dialog .deposit-hash { cursor: help; }
.deposit-transaction-dialog .deposit-balance-button { width: 100%; min-height: 68px; border-radius: 12px; background: #ec5900; border-color: #ec5900; font-size: 20px; }
.deposit-transaction-dialog .deposit-balance-button:hover, .deposit-transaction-dialog .deposit-balance-button:focus { background: #d64f00; border-color: #d64f00; }
.deposit-transaction-dialog .deposit-contact { text-align: center; font-size: 18px; line-height: 30px; padding: 0 0 4px; }
.deposit-transaction-dialog .deposit-contact p { color: #ec5900; margin: 0; }
.deposit-transaction-dialog .deposit-contact a { color: #555; text-decoration: none; overflow-wrap: anywhere; }
.deposit-transaction-dialog .deposit-contact a:hover { text-decoration: underline; }
.deposit-transaction-dialog .deposit-refresh { text-align: center; margin-top: 12px; }
@media (max-width: 540px) {
  .deposit-transaction-dialog .el-dialog__header { padding: 24px 22px 0; }
  .deposit-transaction-dialog .el-dialog__title { font-size: 24px; }
  .deposit-transaction-dialog .el-dialog__body { padding: 20px 22px 24px; }
  .deposit-transaction-dialog .deposit-facts { font-size: 15px; }
  .deposit-transaction-dialog .deposit-result p { font-size: 16px; }
}
</style>
